import { useState, useRef, useEffect, useCallback, ComponentType, ReactNode } from 'react'
import { Search, History, Users, Book, Building2, LayoutGrid, X, SlidersHorizontal } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { recentSearchService, RecentSearch } from '../services/recentSearchService'
import { isCurrentApp, getCurrentApp } from '../utils/appDetection'

interface SearchSuggestion {
  id: string
  type: 'recent' | 'user' | 'document' | 'action'
  text: string
  icon?: ComponentType<{ className?: string }>
  description?: string
  /**
   * Where the thing this suggestion names actually lives. Present when the
   * app's search route knows the destination (dashboard's does), in which
   * case picking the suggestion goes there instead of re-running the search.
   */
  url?: string
}

/** Configuration for the Advanced Search feature */
export interface AdvancedSearchConfig {
  /** Whether to show the advanced search button */
  enabled: boolean
  /** Custom render function for the dropdown content */
  renderContent: (props: {
    onClose: () => void
    onApply: (filters: Record<string, unknown>) => void
  }) => ReactNode
  /** Callback when filters are applied */
  onApply?: (filters: Record<string, unknown>) => void
}

export interface NavbarSearchProps {
  onSearch?: (query: string) => void
  placeholder?: string
  getAuthToken: () => string | null
  apiBaseUrl: string
  /** Path prefixes that should sync the search query from URL. Defaults to ['/dashboard/search'] */
  syncUrlPaths?: string[]
  /** Configuration for advanced search dropdown */
  advancedSearch?: AdvancedSearchConfig
}

// How long a suggestion fetch may run before it is abandoned.
const SEARCH_TIMEOUT_MS = 10000

// Dashboard has its own cross-entity search route: it returns team members,
// companies and workspaces, each with a title and a description.
// The Playbook shape below does not fit it — /users there answers with
// entitlement rows that carry no name or email at all, so every suggestion
// rendered as a bare icon with no text.
const performDashboardSearch = async (
  query: string,
  token: string,
  apiBaseUrl: string
): Promise<SearchSuggestion[]> => {
  const iconForType: Record<string, ComponentType<{ className?: string }>> = {
    member: Users,
    company: Building2,
    workspace: LayoutGrid
  }

  const response = await fetch(
    `${apiBaseUrl}/search?q=${encodeURIComponent(query)}&limit=5`,
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      signal: AbortSignal.timeout(SEARCH_TIMEOUT_MS)
    }
  )

  if (!response.ok) return []

  const json = await response.json()
  if (!json.success || !json.data?.results) return []

  return (json.data.results as any[])
    // A result with no title would render as an icon with no text — the bug
    // this branch exists to fix. Drop it rather than show an empty row.
    .filter(item => typeof item.title === 'string' && item.title.trim())
    .map(item => ({
      id: `${item.type}-${item.id}`,
      type: item.type === 'member' ? ('user' as const) : ('document' as const),
      text: item.title,
      icon: iconForType[item.type] || Search,
      description: item.description || undefined,
      url: typeof item.url === 'string' && item.url ? item.url : undefined
    }))
    .slice(0, 5)
}

// Playbook's navbar search — users and playbooks from the Playbook API.
const performPlaybookSearch = async (
  query: string,
  token: string,
  apiBaseUrl: string
): Promise<SearchSuggestion[]> => {
  const suggestions: SearchSuggestion[] = []

  // Get workspace ID from localStorage
  const app = getCurrentApp()
  const workspaceId = localStorage.getItem(app ? `${app}:workspaceId` : 'workspaceId') || 'ws_default';

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    'X-Workspace-ID': workspaceId
  }

  // Search users and playbooks in parallel
  const [usersResponse, playbooksResponse] = await Promise.all([
    fetch(`${apiBaseUrl}/users?search=${encodeURIComponent(query)}&limit=3`, {
      credentials: 'include',
      headers,
      signal: AbortSignal.timeout(SEARCH_TIMEOUT_MS)
    }),
    fetch(`${apiBaseUrl}/playbooks?search=${encodeURIComponent(query)}&limit=3`, {
      credentials: 'include',
      headers,
      signal: AbortSignal.timeout(SEARCH_TIMEOUT_MS)
    })
  ])

  // Process user results
  if (usersResponse.ok) {
    const userData = await usersResponse.json()
    if (userData.success && userData.data) {
      userData.data.forEach((user: any) => {
        const userName = user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`.trim()
          : user.email

        if (!userName) return

        suggestions.push({
          id: `user-${user.id}`,
          type: 'user',
          text: userName,
          icon: Users,
          description: user.email || user.job_title || 'User'
        })
      })
    }
  }

  // Process playbook results
  if (playbooksResponse.ok) {
    const playbookData = await playbooksResponse.json()
    if (playbookData.success && playbookData.data) {
      playbookData.data.forEach((playbook: any) => {
        if (!playbook.title) return

        suggestions.push({
          id: `playbook-${playbook.id}`,
          type: 'document',
          text: playbook.title,
          icon: Book,
          description: playbook.description || 'Playbook'
        })
      })
    }
  }

  return suggestions.slice(0, 5) // Limit to 5 suggestions
}

// Navbar search - each app answers from its own API, and only its own.
//
// Anything else shows no suggestions rather than falling back to Playbook's
// API: helpdesk has its own search UI, and admin/conductor/form-builder have
// none, so the fallback sent their token to Playbook's origin and offered
// Playbook's users and documents inside an app that cannot open either.
// (Local dev detects the app by port, so a Playbook dev server on a port other
// than 5173 gets no suggestions.)
const performNavbarSearch = async (
  query: string,
  token: string,
  apiBaseUrl: string
): Promise<SearchSuggestion[]> => {
  if (!query.trim()) return []

  try {
    if (isCurrentApp('dashboard')) return await performDashboardSearch(query, token, apiBaseUrl)
    if (isCurrentApp('playbook')) return await performPlaybookSearch(query, token, apiBaseUrl)
    return []
  } catch (error) {
    console.error('Navbar search error:', error)
    return []
  }
}

export function NavbarSearch({ 
  onSearch,
  placeholder,
  getAuthToken,
  apiBaseUrl,
  syncUrlPaths = ['/dashboard/search'],
  advancedSearch
}: NavbarSearchProps) {
  const { t } = useTranslation('translation')
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  // Get initial search query from URL if we're on search page
  const urlQuery = searchParams.get('q') || ''
  const [searchQuery, setSearchQuery] = useState(urlQuery)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([])
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([])
  const [selectedIndex, setSelectedIndex] = useState(-1)
  
  // Cache for search results to avoid flickering on re-focus
  const [searchCache, setSearchCache] = useState<Map<string, SearchSuggestion[]>>(new Map())
  
  // Advanced search dropdown state
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false)
  
  // Track if user is actively editing to prevent URL sync from overwriting
  const isUserEditingRef = useRef(false)

  const searchRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const advancedSearchRef = useRef<HTMLDivElement>(null)

  // Initialize token provider and load recent searches on component mount
  useEffect(() => {
    // Set up token provider for recentSearchService
    recentSearchService.setTokenProvider(getAuthToken)

    const loadRecentSearches = async () => {
      try {
        // Load only navbar-scoped recent searches (not from other contexts like 'content')
        const searches = await recentSearchService.getRecentSearches(5, 'navbar')
        setRecentSearches(searches)
      } catch (error) {
        console.error('Failed to load recent searches:', error)
        setRecentSearches([])
      }
    }
    
    loadRecentSearches()
  }, [getAuthToken])

  // Convert recent searches to search suggestions
  const recentSearchSuggestions: SearchSuggestion[] = (() => {
    // Deduplicate by query text, keeping only the most recent one
    const uniqueSearches = new Map<string, RecentSearch>();
    
    recentSearches.forEach(search => {
      const existing = uniqueSearches.get(search.query);
      if (!existing || new Date(search.lastUsed) > new Date(existing.lastUsed)) {
        uniqueSearches.set(search.query, search);
      }
    });
    
    // Convert to array and map to suggestions
    return Array.from(uniqueSearches.values()).map((search, index) => ({
      id: `recent-${index}`,
      type: 'recent' as const,
      text: search.query,
      icon: History,
      description: t('navbar.notifications.recentSearch')
    }));
  })();

  // Sync search query with URL parameters only on search-related pages
  useEffect(() => {
    const isSearchRelatedPage = syncUrlPaths.some(path => location.pathname.startsWith(path))
    
    if (isSearchRelatedPage) {
      const urlQuery = searchParams.get('q') || ''
      
      // Only sync from URL if user is not actively editing
      if (!isUserEditingRef.current) {
        setSearchQuery(urlQuery)
      }
      
      // Pre-populate cache for URL query to avoid flickering on focus
      if (urlQuery.trim() && !searchCache.has(urlQuery.trim())) {
        const token = getAuthToken() || ''
        performNavbarSearch(urlQuery.trim(), token, apiBaseUrl)
          .then(results => {
            setSearchCache(prev => {
              const newCache = new Map(prev)
              newCache.set(urlQuery.trim(), results)
              
              if (newCache.size > 50) {
                const firstKey = newCache.keys().next().value
                if (firstKey) {
                  newCache.delete(firstKey)
                }
              }
              
              return newCache
            })
          })
          .catch(error => {
            console.error('Error pre-caching URL query results:', error)
          })
      }
    }
  }, [searchParams, location.pathname, getAuthToken, apiBaseUrl, searchCache])

  // Debounced search with caching
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (isDropdownOpen) {
        // If query is empty, show past searches without loading state
        if (!searchQuery.trim()) {
          setSuggestions(recentSearchSuggestions)
          setSelectedIndex(-1)
          return
        }
        
        const query = searchQuery.trim()
        const queryLower = query.toLowerCase()
        
        // Filter recent searches that match the current query
        const matchingRecentSearches = recentSearchSuggestions.filter(
          suggestion => suggestion.text.toLowerCase().includes(queryLower)
        )
        
        // Check if we already have cached results for this query
        if (searchCache.has(query)) {
          const cachedResults = searchCache.get(query)!
          // Combine matching recent searches with cached API results
          const combinedResults = [...matchingRecentSearches, ...cachedResults]
          setSuggestions(combinedResults)
          setSelectedIndex(-1)
          return
        }
        
        // Show matching recent searches immediately while API loads
        if (matchingRecentSearches.length > 0) {
          setSuggestions(matchingRecentSearches)
        }
        
        try {
          const token = getAuthToken() || ''
          const results = await performNavbarSearch(query, token, apiBaseUrl)
          // Combine matching recent searches with API results
          const combinedResults = [...matchingRecentSearches, ...results]
          setSuggestions(combinedResults)
          setSelectedIndex(-1)
          
          // Cache the API results (not combined) for future use
          setSearchCache(prev => {
            const newCache = new Map(prev)
            newCache.set(query, results)
            
            if (newCache.size > 50) {
              const firstKey = newCache.keys().next().value
              if (firstKey) {
                newCache.delete(firstKey)
              }
            }
            
            return newCache
          })
        } catch (error) {
          console.error('Search error:', error)
          // On error, still show matching recent searches if any
          if (matchingRecentSearches.length > 0) {
            setSuggestions(matchingRecentSearches)
          }
        }
      }
    }, searchQuery.trim() ? 300 : 0)

    return () => clearTimeout(timeoutId)
  }, [searchQuery, isDropdownOpen, searchCache, recentSearchSuggestions, getAuthToken, apiBaseUrl])

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
        setSelectedIndex(-1)
      }
      // Close advanced search dropdown when clicking outside
      if (advancedSearchRef.current && !advancedSearchRef.current.contains(event.target as Node)) {
        setIsAdvancedSearchOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const rememberSearch = useCallback(async (query: string) => {
    if (!query.trim()) return

    try {
      await recentSearchService.saveRecentSearch(query.trim(), 'navbar')
      const updatedSearches = await recentSearchService.getRecentSearches(5, 'navbar')
      setRecentSearches(updatedSearches)
    } catch (error) {
      console.error('Error saving recent search:', error)
    }
  }, [])

  const executeSearch = useCallback(async (query: string) => {
    // Reset editing flag when search is executed
    isUserEditingRef.current = false
    
    if (onSearch) {
      onSearch(query)
    }
    
    setIsDropdownOpen(false)
    setSelectedIndex(-1)
    
    // Cache the search results for future focus events
    if (query.trim() && !searchCache.has(query.trim())) {
      try {
        const token = getAuthToken() || ''
        const results = await performNavbarSearch(query.trim(), token, apiBaseUrl)
        setSearchCache(prev => {
          const newCache = new Map(prev)
          newCache.set(query.trim(), results)
          
          if (newCache.size > 50) {
            const firstKey = newCache.keys().next().value
            if (firstKey) {
              newCache.delete(firstKey)
            }
          }
          
          return newCache
        })
      } catch (error) {
        console.error('Error caching search results:', error)
      }
    }
    
    // Save to recent searches
    await rememberSearch(query)
  }, [onSearch, searchCache, getAuthToken, apiBaseUrl, rememberSearch])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()  
    if (selectedIndex >= 0 && suggestions[selectedIndex]) {
      handleSelectSuggestion(suggestions[selectedIndex])
    } else if (searchQuery.trim()) {
      executeSearch(searchQuery.trim())
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isUserEditingRef.current = true
    setSearchQuery(e.target.value)
  }

  const handleInputFocus = () => {
    setIsDropdownOpen(true)
    
    // If we have a query, show matching recent searches + cached results
    if (searchQuery.trim()) {
      const queryLower = searchQuery.trim().toLowerCase()
      const matchingRecentSearches = recentSearchSuggestions.filter(
        suggestion => suggestion.text.toLowerCase().includes(queryLower)
      )
      
      if (searchCache.has(searchQuery.trim())) {
        const cachedResults = searchCache.get(searchQuery.trim())!
        setSuggestions([...matchingRecentSearches, ...cachedResults])
      } else {
        // Show matching recent searches while waiting for API
        setSuggestions(matchingRecentSearches)
      }
    } else {
      // If no query, show recent searches
      setSuggestions(recentSearchSuggestions)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (isDropdownOpen && selectedIndex >= 0 && suggestions[selectedIndex]) {
        handleSelectSuggestion(suggestions[selectedIndex])
      } else if (searchQuery.trim()) {
        executeSearch(searchQuery.trim())
      }
      return
    }

    if (!isDropdownOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1)
        break
      case 'Escape':
        setIsDropdownOpen(false)
        setSelectedIndex(-1)
        inputRef.current?.blur()
        break
    }
  }

  const handleSelectSuggestion = (suggestion: SearchSuggestion) => {
    const searchText = suggestion.text
    setSearchQuery(searchText)

    // A suggestion that names a destination goes there. Re-running the search
    // for its own title would land the operator on a results page holding the
    // one row they just picked.
    if (suggestion.url) {
      isUserEditingRef.current = false
      setIsDropdownOpen(false)
      setSelectedIndex(-1)
      inputRef.current?.blur()
      // Still worth remembering: the next visit offers it before any typing.
      void rememberSearch(searchText)
      navigate(suggestion.url)
      return
    }

    executeSearch(searchText)
  }

  const handleDeleteRecentSearch = async (searchQueryToDelete: string, event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation()
    
    try {
      // Delete all instances of this query across all contexts
      await recentSearchService.deleteRecentSearchItem(searchQueryToDelete, 'all')
      
      const updatedRecentSearches = recentSearches.filter(search => search.query !== searchQueryToDelete)
      setRecentSearches(updatedRecentSearches)
      
      // Update suggestions if we're currently showing recent searches
      if (!searchQuery.trim()) {
        const updatedSuggestions: SearchSuggestion[] = updatedRecentSearches.map((search, index) => ({
          id: `recent-${index}`,
          type: 'recent' as const,
          text: search.query,
          icon: History,
          description: t('navbar.notifications.recentSearch')
        }))
        setSuggestions(updatedSuggestions)
      }
    } catch (error) {
      console.error('Failed to delete recent search:', error)
    }
  }

  const getSuggestionIcon = (suggestion: SearchSuggestion) => {
    const IconComponent = suggestion.icon || Search
    return <IconComponent className="h-4 w-4 text-gray-400 dark:text-gray-500" />
  }

  const handleClearSearch = () => {
    isUserEditingRef.current = false
    setSearchQuery('')
    setSuggestions(recentSearchSuggestions)
    inputRef.current?.focus()
    
    // Call onSearch with empty string to clear URL params and search results
    if (onSearch) {
      onSearch('')
    }
  }

  const handleAdvancedSearchToggle = () => {
    setIsAdvancedSearchOpen(prev => !prev)
    // Close the suggestions dropdown when opening advanced search
    if (!isAdvancedSearchOpen) {
      setIsDropdownOpen(false)
    }
  }

  const handleAdvancedSearchApply = (filters: Record<string, unknown>) => {
    setIsAdvancedSearchOpen(false)
    advancedSearch?.onApply?.(filters)
  }

  const handleAdvancedSearchClose = () => {
    setIsAdvancedSearchOpen(false)
  }

  // Calculate right padding for input based on what buttons are showing
  const hasAdvancedSearch = advancedSearch?.enabled
  const inputPaddingRight = hasAdvancedSearch 
    ? (searchQuery ? 'pr-20' : 'pr-12') 
    : (searchQuery ? 'pr-12' : 'pr-4')

  return (
    <div className="flex-1 max-w-2xl relative" ref={searchRef}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative flex items-center">
          <Search className="absolute left-3 h-5 w-5 text-gray-600 dark:text-gray-400" />
          <input
            id="navbar-search-input"
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || t('navbar.searchPlaceholder')}
            aria-label="Search"
            className={`w-full pl-10 ${inputPaddingRight} py-2.5 bg-primary/5 dark:bg-gray-800 rounded-full field-text placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:bg-white dark:focus:bg-gray-900 focus:shadow-md focus:ring-2 ring-primary transition-[background-color] duration-200`}
            autoComplete="off"
          />
          {/* Right side buttons container */}
          <div className="absolute right-2 flex items-center space-x-1">
            {searchQuery && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                aria-label="Clear search"
              >
                <X className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" />
              </button>
            )}
            {hasAdvancedSearch && (
              <div ref={advancedSearchRef} className="relative">
                <button
                  type="button"
                  onClick={handleAdvancedSearchToggle}
                  className={`p-1.5 rounded-full transition-colors ${
                    isAdvancedSearchOpen 
                      ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-[hsl(var(--primary-hover))]'
                      : 'hover:bg-gray-200 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                  aria-label="Advanced search"
                  title="Advanced search"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                </button>
                
                {/* Advanced Search Dropdown */}
                {isAdvancedSearchOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg z-[10001] min-w-80">
                    {advancedSearch.renderContent({
                      onClose: handleAdvancedSearchClose,
                      onApply: handleAdvancedSearchApply
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </form>

      {/* Search Dropdown */}
      {isDropdownOpen && (suggestions.length > 0 || searchQuery.trim()) && (
        <div 
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-900 rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.15)] dark:shadow-[0_2px_8px_rgba(255,255,255,0.15)] z-[10000] max-h-96 overflow-y-auto"
        >
          {suggestions.length > 0 ? (
            <div className="p-2">
              {/*
              {!searchQuery && (
                <div className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-800">
                  {t('navbar.recentSearches')}
                </div>
              )}
              */}
              {suggestions.map((suggestion, index) => (
                <button
                  key={suggestion.id}
                  className={`group w-full px-4 py-2.5 rounded-md text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center space-x-3 transition-colors ${
                    index === selectedIndex ? 'bg-primary/5 dark:bg-primary/10 border-r-2 border-primary' : ''
                  }`}
                  onClick={() => handleSelectSuggestion(suggestion)}
                >
                  <div className="flex-shrink-0">
                    {getSuggestionIcon(suggestion)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                      {suggestion.text}
                    </div>
                  </div>
                  {suggestion.type === 'recent' && (
                    <div
                      className="flex-shrink-0 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                      onClick={(e) => handleDeleteRecentSearch(suggestion.text, e)}
                      title="Remove from recent searches"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleDeleteRecentSearch(suggestion.text, e)
                        }
                      }}
                    >
                      <X className="h-3 w-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          ) : searchQuery.trim() ? (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
              {t('navbar.noResults')}
            </div>
          ) : null}
          
          {/* Search Tips */}
          {/*
          <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-2 bg-gray-50 dark:bg-gray-800">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium">{t('common.tip')}</span> {t('navbar.searchTip')}
            </div>
          </div>
          */}
        </div>
      )}
    </div>
  )
}
